import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  Button,
  Flex,
  Heading,
  Item,
  Key,
  Picker,
  Switch,
} from "@adobe/react-spectrum";
import { MediaDevice } from "../../types/webrtc.type";
import { useEffect, useState } from "react";
import { RING_TYPES, testRingTypes } from "../../constants/constants";
import Emitter from "../../emitter";
import { API_UPDATE_RING_MEDIA_DEVICE } from "../../constants/events";
import storeRTK from "../../store/storeRTK";
import { updateRingOnDifferentDevice } from "../../actions/webrtc";
import {
  testCallbackPlayer,
  testcallplayer,
  testcasePlayer,
  testchatPlayer,
  testVoicemailPlayer,
} from "../../amazon-connect/constants";

type MappedMediaDevice = {
  id: string;
  name: string;
  displayName: string;
  type: string;
};

const AccountSettings = () => {
  const webrtc = useSelector((state: RootState) => state.webrtc);
  const { ringtoneOutoutDeviceId, mediaDevices, ringOnDifferentDevice } =
    webrtc;
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [ringOnDiffDevice, setRingOnDiffDevice] = useState(
    webrtc.ringOnDifferentDevice
  );
  const [devices, setDevices] = useState([]);
  const [selectedTestPlayRing, setSelectedTestPlayRing] = useState("CALL");

  useEffect(() => {
    const mappedDevices =
      mediaDevices
        ?.map((device: MediaDevice) => {
          return {
            id: device.deviceId,
            name: device.label,
            displayName: device.label,
            type: device.kind,
          };
        })
        .filter(
          (device: MappedMediaDevice) =>
            device.name &&
            device.id &&
            device.type === "audiooutput" &&
            !device?.name.toLowerCase().includes("virtual")
        ) || [];
    setDevices(mappedDevices);
    if (!selectedDeviceId) {
      setSelectedDeviceId(
        (
          mappedDevices.find((device: MappedMediaDevice) =>
            device?.name.toLowerCase().includes("default")
          ) || { id: "" }
        ).id
      );
    }
  }, [mediaDevices]);

  useEffect(() => {
    setRingOnDiffDevice(ringOnDifferentDevice);
  }, [ringOnDifferentDevice]);

  const handleSelectDevice = (selectedDevice: string) => {
    Emitter.emit(API_UPDATE_RING_MEDIA_DEVICE, {
      body: { deviceId: selectedDevice },
    });
  };

  useEffect(() => {
    if (ringtoneOutoutDeviceId && devices.length > 0) {
      if (
        devices.findIndex(
          (device: MappedMediaDevice) => device.id === ringtoneOutoutDeviceId
        ) > -1
      ) {
        setSelectedDeviceId(ringtoneOutoutDeviceId);
      } else {
        if (!selectedDeviceId) {
          setSelectedDeviceId(
            (
              devices.find((device: MappedMediaDevice) =>
                device?.name.toLowerCase().includes("default")
              ) || { id: "" }
            ).id
          );
        }
      }
    } else {
      if (!selectedDeviceId) {
        setSelectedDeviceId(
          (
            devices.find((device: MappedMediaDevice) =>
              device?.name.toLowerCase().includes("default")
            ) || { id: "" }
          ).id
        );
      }
    }
  }, [ringtoneOutoutDeviceId, devices]);

  const handleRingChbox = (choice: boolean) => {
    storeRTK.dispatch(updateRingOnDifferentDevice(choice));
    setRingOnDiffDevice(choice);
  };

  const playCallHandler = () => {
    const playerMap: Record<string, { play: () => void }> = {
      [RING_TYPES.CALL]: testcallplayer,
      [RING_TYPES.CHAT]: testchatPlayer,
      [RING_TYPES.CASE]: testcasePlayer,
      [RING_TYPES.CALLBACK]: testCallbackPlayer,
      [RING_TYPES.VOICEMAIL]: testVoicemailPlayer,
    };

    // Use the selected player or default to `testcallplayer`
    (playerMap[selectedTestPlayRing] || testcallplayer).play();
  };

  return (
    <Flex direction={"column"} marginX={"1.615%"}>
      <Heading>Audio devices</Heading>
      <Picker
        label="Test ring sound"
        defaultSelectedKey={selectedTestPlayRing}
        width={"20.26%"}
        onSelectionChange={(key: Key | null) => {
          setSelectedTestPlayRing(key ? key.toString() : "");
        }}
      >
        {testRingTypes.map((type: { name: string; id: string }) => (
          <Item key={type.id}>{type.name}</Item>
        ))}
      </Picker>
      <Switch
        aria-label="switch-ring-device"
        isSelected={ringOnDiffDevice}
        onChange={(selected) => handleRingChbox(selected)}
        marginTop={"2.957%"}
        marginBottom={"1.68%"}
      >
        Play ring on different device
      </Switch>
      <Picker
        label="Ring on"
        onSelectionChange={(key: Key | null) =>
          handleSelectDevice(key as string)
        }
        selectedKey={selectedDeviceId}
        defaultSelectedKey={selectedDeviceId}
        width={"20.26%"}
        marginBottom={"30px"}
        isDisabled={!ringOnDiffDevice}
      >
        {devices.map((device: MappedMediaDevice) => (
          <Item key={device.id} textValue={device.name}>
            {device.displayName}
          </Item>
        ))}
      </Picker>
      <Button
        variant="accent"
        UNSAFE_style={{
          width: "98px",
          height: "32px",
          font: "normal normal bold 14px/17px Adobe Clean",
        }}
        onPress={playCallHandler}
      >
        Ring sound
      </Button>
    </Flex>
  );
};

export default AccountSettings;
